import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { Container, Row, Col, Button, Card, Form, Table, Modal, Alert } from 'react-bootstrap';
import { FaBullhorn, FaCheck, FaUserCheck, FaAngleUp, FaAngleDown, FaGem } from 'react-icons/fa';
import HeaderAzul from '~/Components/HeaderAzul/HeaderAzul';
import NivelBadge from '~/Components/Lancamento/Encaminhamento/NivelBadge';
import qs from 'qs';

import Loading from '~/Components/Loading';
import ModalProspecto from '~/Components/AdmVendas/ModalProspecto';
import {
    fetchAssinatura,
    recallAssinatura,
    entradaEspecifica,
    marcarComparecimento,
    chamarProximaAssinatura,
} from '~/Store/Lancamento/Encaminhamento/actions';

const EncaminhamentoAssinatura = () => {
    const location = useLocation();
    const dispatch = useDispatch();

    // Redux state
    const senhas = useSelector((state) => state.encaminhamento.assinatura.senhas);
    const relatorio = useSelector((state) => state.encaminhamento.assinatura.relatorio);
    const isLoading = useSelector((state) => state.encaminhamento.assinatura.isLoading);

    // Local state
    const [codigoFamilia, setCodigoFamilia] = useState(null);
    const [senhaEspecifica, setSenhaEspecifica] = useState('');
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [senhaParaChamar, setSenhaParaChamar] = useState(null);
    const [pesquisa, setPesquisa] = useState('');
    const [ordenacao, setOrdenacao] = useState({ coluna: null, direcao: 'asc' });
    const [colunaHover, setColunaHover] = useState(null);
    const [chamarDiamante, setChamarDiamante] = useState(true);
    const [modalProspecto, setModalProspecto] = useState({ show: false, codigo: null });
    const [senhaRechamada, setSenhaRechamada] = useState(null);
    const recallTimeoutRef = useRef(null);

    const colunas = [
        { chave: 'senha', label: 'Senha', tipo: 'numero' },
        { chave: 'data_chamada', label: 'Data Atendimento', tipo: 'data' },
        { chave: 'cliente', label: 'Cliente', tipo: 'texto' },
        { chave: 'data_fim', label: 'Data Fim Atendimento', tipo: 'data' },
        { chave: 'numero_mesa', label: 'Mesa', tipo: 'numero' },
        { chave: 'data_entrada', label: 'Data de Entrada', tipo: 'data' },
        { chave: 'data_chamada_assinatura', label: 'Última Chamada', tipo: 'data' },
    ];

    // Carregar dados
    const carregarDados = useCallback((familia) => {
        if (familia) {
            dispatch(fetchAssinatura(familia));
        }
    }, [dispatch]);

    // Inicialização
    useEffect(() => {
        const params = qs.parse(location.search, { ignoreQueryPrefix: true });
        const familia = parseInt(params.familia, 10);

        if (familia) {
            setCodigoFamilia(familia);
            carregarDados(familia);
        }
    }, [location.search, carregarDados]);

    let senhasProcessadas = [...senhas];

    if (pesquisa.trim()) {
        const termo = pesquisa.toLowerCase().trim();
        senhasProcessadas = senhasProcessadas.filter((s) => {
            const senhaStr = s.senha != null ? s.senha.toString().trim() : '';
            const matchSenha = senhaStr.includes(termo);
            const clienteStr = s.cliente || '';
            const matchCliente = clienteStr.toLowerCase().includes(termo);
            return matchSenha || matchCliente;
        });
    }

    if (ordenacao.coluna) {
        const { coluna, direcao } = ordenacao;
        const colDef = colunas.find(c => c.chave === coluna);
        const tipo = colDef ? colDef.tipo : 'texto';

        senhasProcessadas.sort((a, b) => {
            const valA = a[coluna];
            const valB = b[coluna];

            if (valA == null && valB == null) return 0;
            if (valA == null) return 1;
            if (valB == null) return -1;

            let comp = 0;
            if (tipo === 'numero') {
                const numA = Number(valA);
                const numB = Number(valB);
                comp = numA - numB;
            } else if (tipo === 'data') {
                const dataA = new Date(valA);
                const dataB = new Date(valB);
                comp = dataA.getTime() - dataB.getTime();
            } else {
                const strA = String(valA).toLowerCase();
                const strB = String(valB).toLowerCase();
                if (strA < strB) comp = -1;
                else if (strA > strB) comp = 1;
                else comp = 0;
            }

            return direcao === 'asc' ? comp : -comp;
        });
    }

    const handleOrdenacao = (coluna) => {
        setOrdenacao((prev) => {
            if (prev.coluna === coluna) {
                if (prev.direcao === 'asc') return { coluna, direcao: 'desc' };
                return { coluna: null, direcao: 'asc' };
            }
            return { coluna, direcao: 'asc' };
        });
    };

    const getIconeOrdenacao = (coluna) => {
        if (ordenacao.coluna !== coluna) return '';
        return ordenacao.direcao === 'asc' ? <FaAngleUp /> : <FaAngleDown />;
    };

    const formatarDataCurta = (data) => {
        if (!data) return '-';
        const d = new Date(data);
        const dia = String(d.getDate()).padStart(2, '0');
        const mes = String(d.getMonth() + 1).padStart(2, '0');
        const hora = d.toLocaleTimeString('pt-BR');
        return `${dia}/${mes} ${hora}`;
    };

    const getRowStyle = (senha) => {
        if (senha.data_chamada_assinatura) {
            return { backgroundColor: '#C8E6C9' };
        }
        if (senha.comparecimento_assinatura) {
            return { backgroundColor: '#FFE0B2' };
        }
        if (senha.atendimento_finalizado) {
            return { backgroundColor: '#BBDEFB' };
        }
        return { backgroundColor: '#E0E0E0' };
    };

    const handleChamar = (linhaSenha) => {
        setShowConfirmModal(true);
        setSenhaParaChamar(linhaSenha);
    };

    const handleConfirmChamar = () => {
        if (senhaParaChamar) {
            const ehDiamante = senhaParaChamar.atendimento_preferencial === true;
            dispatch(entradaEspecifica(codigoFamilia, senhaParaChamar.senha, ehDiamante));
        }
        setShowConfirmModal(false);
        setSenhaParaChamar(null);
    };

    const handleCancelChamar = () => {
        setShowConfirmModal(false);
        setSenhaParaChamar(null);
    };

    const handleRecall = (codigoSenha) => {
        dispatch(recallAssinatura(codigoSenha, codigoFamilia));
        setSenhaRechamada(codigoSenha);
        clearTimeout(recallTimeoutRef.current);
        recallTimeoutRef.current = setTimeout(() => {
            setSenhaRechamada(null);
        }, 5000);
    };

    const handleMarcarComparecimento = (codigoSenha) => {
        dispatch(marcarComparecimento(codigoSenha, codigoFamilia));
    };

    const handleEntradaEspecifica = (e) => {
        e.preventDefault();

        if (!senhaEspecifica) {
            return;
        }

        const senha = parseInt(senhaEspecifica, 10);
        if (isNaN(senha)) {
            return;
        }

        dispatch(entradaEspecifica(codigoFamilia, senha, chamarDiamante));
        setSenhaEspecifica('');
    };

    const handleSenhaEspecificaChange = (e) => {
        const value = e.target.value.replace(/\D/g, '');
        setSenhaEspecifica(value);
    };

    const tableStyles = {
        wrapper: {
            borderRadius: '8px',
            border: '1px solid #dee2e6',
            maxHeight: '500px',
            overflowY: 'auto',
        },
        header: {
            backgroundColor: '#0747a6',
            color: '#fff',
            borderColor: '#0747a6',
            cursor: 'pointer',
            userSelect: 'none',
            transition: 'background-color 0.2s',
            position: 'sticky',
            top: 0,
            zIndex: 2,
        },
        headerHover: {
            backgroundColor: '#053580',
        },
    };

    if (!codigoFamilia) {
        return (
            <Container className="py-4">
                <Alert variant="warning">
                    Nenhum empreendimento selecionado. Adicione <strong>?familia=CODIGO</strong> na URL.
                </Alert>
            </Container>
        );
    }

    return (
        <Container fluid className="p-4">
            <style>
                {`
                    .tabela-assinatura tbody tr:hover {
                        filter: brightness(0.95);
                        cursor: default;
                    }
                    .tabela-assinatura tbody tr.recall-row {
                        animation: recall-row-piscar 0.8s ease-in-out infinite;
                    }
                    @keyframes recall-row-piscar {
                        0%   { box-shadow: inset 0 0 0 100vw transparent; }
                        50%  { box-shadow: inset 0 0 0 100vw #0747a6; color: #fff; }
                        100% { box-shadow: inset 0 0 0 100vw transparent; }
                    }
                    .search-input::placeholder {
                        color: #6c757d;
                        opacity: 0.8;
                    }

                    @media (max-width: 768px) {
                        .tabela-assinatura thead {
                            display: none;
                        }
                        .tabela-assinatura tbody tr {
                            display: block;
                            margin-bottom: 1rem;
                            border: 1px solid #dee2e6;
                            border-radius: 8px;
                            overflow: hidden;
                        }
                        .tabela-assinatura tbody td {
                            display: flex;
                            justify-content: space-between;
                            align-items: center;
                            padding: 0.75rem 1rem;
                            border: none;
                            border-bottom: 1px solid #eee;
                        }
                        .tabela-assinatura tbody td:last-child {
                            border-bottom: none;
                        }
                        .tabela-assinatura tbody td::before {
                            content: attr(data-label);
                            font-weight: 600;
                            color: #0747a6;
                            margin-right: 1rem;
                        }
                    }
                `}
            </style>

            <HeaderAzul
                title="Encaminhamento Assinatura"
                contemPesquisa={true}
                pesquisa={pesquisa}
                setPesquisa={setPesquisa}
                searchPlaceholder="Buscar por senha ou cliente..."
            />

            <Row className="mb-4 align-items-stretch">
                <Col md={3} className="d-flex">
                    <div className="d-flex align-items-center p-3 bg-white rounded shadow-sm w-100">
                        <div className="mr-3" style={{ fontSize: '2rem', opacity: 0.9, color: '#555' }}>
                            <i className="fa fa-user" />
                        </div>
                        <div className="d-flex flex-column">
                            <span className="font-weight-bold text-dark" style={{ fontSize: '1.5rem', lineHeight: 1 }}>
                                {relatorio.clientes_aptos}
                            </span>
                            <span className="text-muted" style={{ fontSize: '0.8rem', marginTop: '3px' }}>
                                Clientes aptos
                            </span>
                        </div>
                    </div>
                </Col>
                <Col md={3} className="d-flex">
                    <div className="d-flex align-items-center p-3 bg-white rounded shadow-sm w-100">
                        <div className="mr-3" style={{ fontSize: '2rem', opacity: 0.9, color: '#555' }}>
                            <FaBullhorn />
                        </div>
                        <div className="d-flex flex-column">
                            <span className="font-weight-bold text-dark" style={{ fontSize: '1.5rem', lineHeight: 1 }}>
                                {relatorio.senhas_chamadas}
                            </span>
                            <span className="text-muted" style={{ fontSize: '0.8rem', marginTop: '3px' }}>
                                Senhas chamadas
                            </span>
                        </div>
                    </div>
                </Col>
                <Col md={3} className="d-flex">
                    <div className="d-flex align-items-center p-3 bg-white rounded shadow-sm w-100">
                        <div className="mr-3" style={{ fontSize: '2rem', opacity: 0.9, color: '#555' }}>
                            <FaUserCheck />
                        </div>
                        <div className="d-flex flex-column">
                            <span className="font-weight-bold text-dark" style={{ fontSize: '1.5rem', lineHeight: 1 }}>
                                {relatorio.senhas_atendidas}
                            </span>
                            <span className="text-muted" style={{ fontSize: '0.8rem', marginTop: '3px' }}>
                                Senhas atendidas
                            </span>
                        </div>
                    </div>
                </Col>
                <Col md={3} className="d-flex">
                    <div className="d-flex align-items-center p-3 bg-white rounded shadow-sm w-100">
                        <div className="mr-3" style={{ fontSize: '2rem', opacity: 0.9, color: '#555' }}>
                            <FaCheck />
                        </div>
                        <div className="d-flex flex-column">
                            <span className="font-weight-bold text-dark" style={{ fontSize: '1.5rem', lineHeight: 1 }}>
                                {relatorio.finalizados}
                            </span>
                            <span className="text-muted" style={{ fontSize: '0.8rem', marginTop: '3px' }}>
                                Finalizados
                            </span>
                        </div>
                    </div>
                </Col>
            </Row>

            <Row className="mb-4 align-items-stretch">
                <Col md={6}>
                    <Card className="h-100">
                        <Card.Body className="d-flex align-items-center">
                            <div className="d-flex flex-column align-items-center mr-3">
                                <Form.Check
                                    type="switch"
                                    id="toggle-nivel-entrada"
                                    checked={chamarDiamante}
                                    onChange={() => setChamarDiamante(!chamarDiamante)}
                                    label=""
                                    className="mb-1"
                                />
                                <small
                                    className="font-weight-bold"
                                    style={{ color: chamarDiamante ? '#1565C0' : '#FF8F00', fontSize: '0.75rem', lineHeight: 1 }}
                                >
                                    {chamarDiamante ? 'Diamante' : 'Topázio'}
                                </small>
                            </div>
                            <Form onSubmit={handleEntradaEspecifica} className="d-flex flex-grow-1">
                                <Form.Control
                                    type="text"
                                    placeholder="Número da senha"
                                    value={senhaEspecifica}
                                    onChange={handleSenhaEspecificaChange}
                                    maxLength={4}
                                    className="mr-2"
                                />
                                <Button type="submit" variant="primary" style={{ whiteSpace: 'nowrap' }}>
                                    Dar entrada
                                </Button>
                            </Form>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={6}>
                    <Card className="h-100">
                        <Card.Body className="d-flex align-items-center justify-content-center">
                            <Button
                                variant="success"
                                size="lg"
                                onClick={() => dispatch(chamarProximaAssinatura(codigoFamilia))}
                                className="font-weight-bold text-uppercase py-3 w-100"
                                style={{ fontSize: '1.2rem' }}
                            >
                                Chamar Próxima Senha
                            </Button>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            <Row className="mb-3">
                <Col>
                    <div className="d-flex align-items-center flex-wrap">
                        <span className="mr-1" style={{ width: '16px', height: '16px', backgroundColor: '#E0E0E0', display: 'inline-block', borderRadius: '3px' }}></span>
                        <small className="mr-3">Venda realizada</small>
                        <span className="mr-1" style={{ width: '16px', height: '16px', backgroundColor: '#BBDEFB', display: 'inline-block', borderRadius: '3px' }}></span>
                        <small className="mr-3">Atendimento encerrado</small>
                        <span className="mr-1" style={{ width: '16px', height: '16px', backgroundColor: '#FFE0B2', display: 'inline-block', borderRadius: '3px' }}></span>
                        <small className="mr-3">Check-in realizado</small>
                        <span className="mr-1" style={{ width: '16px', height: '16px', backgroundColor: '#C8E6C9', display: 'inline-block', borderRadius: '3px' }}></span>
                        <small>Encaminhado</small>
                    </div>
                </Col>
            </Row>

            <Row>
                <Col>
                    {isLoading && senhas.length === 0 ? (
                        <Loading />
                    ) : (
                        <>
                            {senhas.length > 0 && senhasProcessadas.length === 0 && (
                                <Alert variant="warning">
                                    Nenhum resultado encontrado para "{pesquisa}".
                                </Alert>
                            )}

                            {senhasProcessadas.length > 0 && (
                                <>
                                    <div style={tableStyles.wrapper}>
                                        <Table bordered hover className="mb-0 tabela-assinatura">
                                        <thead>
                                            <tr>
                                                {colunas.map((col) => (
                                                    <th
                                                        key={col.chave}
                                                        style={{
                                                            ...tableStyles.header,
                                                            ...(colunaHover === col.chave ? tableStyles.headerHover : {}),
                                                        }}
                                                        onClick={() => handleOrdenacao(col.chave)}
                                                        onMouseEnter={() => setColunaHover(col.chave)}
                                                        onMouseLeave={() => setColunaHover(null)}
                                                    >
                                                        {col.label}
                                                        <span style={{ fontSize: '0.75rem', marginLeft: '4px' }}>
                                                            {getIconeOrdenacao(col.chave)}
                                                        </span>
                                                    </th>
                                                ))}
                                                <th style={{ ...tableStyles.header, cursor: 'default' }}>Ação</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {senhasProcessadas.map((senha, index) => (
                                                <tr
                                                    key={`${senha.codigo}-${index}`}
                                                    style={getRowStyle(senha)}
                                                    className={senha.codigo === senhaRechamada ? 'recall-row' : ''}
                                                >
                                                    <td data-label="Senha">
                                                        {senha.senha > 0 ? (
                                                            <NivelBadge isDiamante={senha.atendimento_preferencial} senha={senha.senha} />
                                                        ) : '-'}
                                                    </td>
                                                    <td data-label="Data Atendimento">{formatarDataCurta(senha.data_chamada)}</td>
                                                    <td data-label="Cliente">
                                                        {senha.atendimento_preferencial == null ? (
                                                            <a
                                                                href="#"
                                                                onClick={(e) => {
                                                                    e.preventDefault();
                                                                    setModalProspecto({ show: true, codigo: senha.codigo_prospecto });
                                                                }}
                                                                style={{ fontWeight: 'bold', color: '#0747a6' }}
                                                            >
                                                                {senha.cliente}
                                                            </a>
                                                        ) : senha.cliente}
                                                    </td>
                                                    <td data-label="Data Fim Atendimento">{formatarDataCurta(senha.data_fim)}</td>
                                                    <td data-label="Mesa">{senha.numero_mesa || '-'}</td>
                                                    <td data-label="Data de Entrada">{formatarDataCurta(senha.data_entrada)}</td>
                                                    <td data-label="Última Chamada">{formatarDataCurta(senha.data_chamada_assinatura)}</td>
                                                    <td data-label="Ação" style={{ whiteSpace: 'nowrap' }}>
                                                        {senha.data_chamada_assinatura ? (
                                                            <>
                                                                <Button
                                                                    variant="warning"
                                                                    size="sm"
                                                                    onClick={() => handleRecall(senha.codigo)}
                                                                    title="Chamar novamente"
                                                                    className="mr-1"
                                                                >
                                                                    <FaBullhorn />
                                                                </Button>
                                                                {!senha.compareceu_a_assinatura && (
                                                                    <Button
                                                                        variant="info"
                                                                        size="sm"
                                                                        onClick={() => handleMarcarComparecimento(senha.codigo)}
                                                                        title="Marcar comparecimento"
                                                                    >
                                                                        <FaUserCheck />
                                                                    </Button>
                                                                )}
                                                            </>
                                                        ) : (
                                                            <Button
                                                                variant="success"
                                                                size="sm"
                                                                onClick={() => handleChamar(senha)}
                                                                title="Chamar"
                                                            >
                                                                <FaCheck />
                                                            </Button>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                    </div>
                                </>
                            )}

                            {senhas.length === 0 && (
                                <Alert variant="info">Nenhuma senha disponível para assinatura.</Alert>
                            )}
                        </>
                    )}
                </Col>
            </Row>

            <Modal show={showConfirmModal} onHide={handleCancelChamar} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Confirmação</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    Confirma chamada para assinatura?
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleCancelChamar}>
                        Cancelar
                    </Button>
                    <Button variant="primary" onClick={handleConfirmChamar}>
                        Confirmar
                    </Button>
                </Modal.Footer>
            </Modal>

            {modalProspecto.show && (
                <ModalProspecto
                    mostrarModal={modalProspecto.show}
                    setMostrarModal={(show) => {
                        setModalProspecto({ show, codigo: show ? modalProspecto.codigo : null });
                        if (!show) {
                            carregarDados(codigoFamilia);
                        }
                    }}
                    codigoProspecto={modalProspecto.codigo}
                    modoAssinatura={true}
                />
            )}
        </Container>
    );
};

export default EncaminhamentoAssinatura;
